/**
  Wrap the authentication functions so that react can "listen" to the changes
*/

import React, {
  useCallback,
  useState,
  createContext,
  useContext,
  useEffect,
} from 'react'
import {
  login as authLogin,
  logout as authLogout,
  currentUser as authCurrentUser,
  isAuthenticated as authIsAuthenticated,
  testToken as authTestToken,
  User,
} from 'utils/authenticationService'

interface authState {
  currentUser: User | null
  isAuthenticated: boolean
}

export interface AuthContextValues {
  currentUser: User | null
  isAuthenticated: boolean
  login: typeof authLogin
  logout: typeof authLogout
}

const defaultContextValue: AuthContextValues = {
  currentUser: authCurrentUser(),
  isAuthenticated: authIsAuthenticated(),
  login: (_user, _password) => Promise.resolve(false),
  logout: () => {},
}

export const AuthContext = createContext<AuthContextValues>(defaultContextValue)

export const AuthProvider: React.FunctionComponent = ({ children }) => {
  const [{ currentUser, isAuthenticated }, setAuth] = useState<authState>({
    currentUser: defaultContextValue.currentUser,
    isAuthenticated: defaultContextValue.isAuthenticated,
  })

  useEffect(() => {
    if (isAuthenticated) {
      authTestToken().then((tokenValid) => {
        if (!tokenValid) {
          authLogout()
        }
      })
    }
  }, [isAuthenticated])

  const login = useCallback(
    async (user: string, password: string) => {
      if (currentUser) {
        return true
      }

      const res = await authLogin(user, password)

      setAuth({
        currentUser: authCurrentUser(),
        isAuthenticated: authIsAuthenticated(),
      })
      return res
    },
    [setAuth, currentUser]
  )

  const logout = useCallback(() => {
    authLogout()
    setAuth({
      currentUser: authCurrentUser(),
      isAuthenticated: authIsAuthenticated(),
    })
  }, [setAuth])

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isAuthenticated,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuthentication = () => useContext(AuthContext)
